import { requireEnv } from '@/lib/env'
/**
 * Chat Response Hook
 *
 * Sends messages to the AI engine backend and returns the response.
 * Uses the self-hosted ai-engine (NEXT_PUBLIC_AI_ENGINE_URL) at /widget/chat.
 */

import { useState, useCallback, useRef } from 'react'

const AI_ENGINE_URL = requireEnv(process.env.NEXT_PUBLIC_AI_ENGINE_URL, 'NEXT_PUBLIC_AI_ENGINE_URL')

export interface UseStreamingResponseReturn {
  isProcessing: boolean
  error: string | null
  sendMessage: (sessionCode: string, message: string) => Promise<string | null>
  cancelRequest: () => void
}

export function useStreamingResponse(
  onFunctionCall?: (name: string, result?: unknown) => void
): UseStreamingResponseReturn {
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const cancelRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setIsProcessing(false)
  }, [])

  const sendMessage = useCallback(
    async (sessionCode: string, message: string): Promise<string | null> => {
      cancelRequest()
      setIsProcessing(true)
      setError(null)
      abortControllerRef.current = new AbortController()

      try {
        const response = await fetch(`${AI_ENGINE_URL}/widget/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionCode, message }),
          signal: abortControllerRef.current.signal,
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.error || `HTTP error ${response.status}`)
        }

        const data = await response.json()

        // Notify about tool calls if callback provided
        if (onFunctionCall && data.toolCalls) {
          for (const tool of data.toolCalls) {
            onFunctionCall(typeof tool === 'string' ? tool : tool.name, typeof tool === 'object' ? tool.result : undefined)
          }
        }

        if (data.error) {
          setError(data.error)
          setIsProcessing(false)
          return null
        }

        setIsProcessing(false)
        return data.content || null
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return null
        const errorMessage = err instanceof Error ? err.message : 'Failed to send message'
        setError(errorMessage)
        setIsProcessing(false)
        return null
      }
    },
    [cancelRequest, onFunctionCall]
  )

  return { isProcessing, error, sendMessage, cancelRequest }
}
